import * as React from 'react';

import {
  Button,
  TextField,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogMain,
  DialogFooter,
  DialogTrigger,
} from '@rematter/ui-web';
import { useSetAtom } from 'jotai';
import { Trash2 } from 'lucide-react';

import {
  $updateFreightCharge,
  $removeFreightCharge,
  FreightChargeForm,
} from '~/modules/trade/_state/loadReconciliation.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface FreightChargeDialogProps {
  freightCharge: FreightChargeForm;
  isOpen: boolean;
  onClose: () => void;
  disabled?: boolean;
}

export const FreightChargeDialog = ({
  freightCharge,
  isOpen,
  onClose,
  disabled = false,
}: FreightChargeDialogProps) => {
  const t = useTranslations();
  const updateFreightCharge = useSetAtom($updateFreightCharge);
  const removeFreightCharge = useSetAtom($removeFreightCharge);

  // Local state for dialog form
  const [localNotes, setLocalNotes] = React.useState(freightCharge.notes ?? '');

  const handleSave = () => {
    updateFreightCharge(freightCharge.id, {
      notes: localNotes,
    });
    onClose();
  };

  const handleDelete = () => {
    removeFreightCharge(freightCharge.id);
    onClose();
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogOverlay className="@FreightChargeDialog">
        <DialogContent
          position="right"
          className="w-[600px]"
          dialogClassName="grid-rows-[auto_1fr_auto]"
        >
          <DialogHeader>
            <DialogTitle>
              {t.materialsReconciliation.dialogs.freightChargeTitle}
            </DialogTitle>
          </DialogHeader>

          <DialogMain className="flex-1">
            <div className="space-y-6">
              <div>
                <div className="mb-3 text-sm font-medium text-neutral/90">
                  {t.materialsReconciliation.dialogs.notes}
                </div>
                <TextField
                  value={localNotes}
                  onChange={setLocalNotes}
                  textArea
                  placeholder={
                    t.materialsReconciliation.dialogs.freightNotesPlaceholder
                  }
                  className="w-full"
                  isDisabled={disabled}
                />
              </div>
            </div>
          </DialogMain>

          <DialogFooter>
            {!disabled && (
              <div className="flex w-full flex-row justify-between gap-2">
                <Button
                  variant="outline"
                  color="failure"
                  icon={<Trash2 size={18} />}
                  onPress={handleDelete}
                >
                  {t.materialsReconciliation.dialogs.deleteFreight}
                </Button>
                <Button onPress={handleSave}>{t.common.save}</Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </DialogOverlay>
    </DialogTrigger>
  );
};
